import { useEffect, useState } from 'react';

import type { CloudApiClient, CloudBudget } from './api-client';
import {
  clearCloudBudgetId,
  loadCloudBudgetId,
  readCloudBudgetId,
  saveCloudBudgetId,
} from './budget-cache';

type BindingState = Readonly<{
  budgets: readonly CloudBudget[];
  error: Error | null;
  selectedBudgetId: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
}>;

export type CloudBudgetBinding = BindingState &
  Readonly<{
    createBudget: () => Promise<void>;
    reload: () => Promise<void>;
    selectBudget: (budgetId: string) => void;
  }>;

export function useCloudBudgetBinding(
  api: CloudApiClient,
  subject: string,
  workspaceId: string | null,
  localBudgetId: string | null,
  localBudgetName: string | null,
  isOffline: boolean,
): CloudBudgetBinding {
  const [state, setState] = useState<BindingState>(() =>
    initialState(subject, workspaceId, localBudgetId),
  );

  async function reload(): Promise<void> {
    if (workspaceId === null || localBudgetId === null || isOffline) {
      setState(initialState(subject, workspaceId, localBudgetId));
      return;
    }
    setState(current => ({ ...current, error: null, status: 'loading' }));
    try {
      const budgets = await api.listBudgets(workspaceId);
      setState({
        budgets,
        error: null,
        selectedBudgetId: resolveCloudBudgetId(
          subject,
          workspaceId,
          localBudgetId,
          budgets,
        ),
        status: 'ready',
      });
    } catch (error) {
      setState(current => ({
        ...current,
        error: toError(error),
        status: 'error',
      }));
    }
  }

  useEffect(() => {
    let isCurrent = true;
    if (workspaceId === null || localBudgetId === null || isOffline) {
      setState(initialState(subject, workspaceId, localBudgetId));
      return;
    }
    setState(current => ({ ...current, error: null, status: 'loading' }));
    void api
      .listBudgets(workspaceId)
      .then(budgets => {
        if (!isCurrent) return;
        setState({
          budgets,
          error: null,
          selectedBudgetId: resolveCloudBudgetId(
            subject,
            workspaceId,
            localBudgetId,
            budgets,
          ),
          status: 'ready',
        });
      })
      .catch(error => {
        if (!isCurrent) return;
        setState(current => ({
          ...current,
          error: toError(error),
          status: 'error',
        }));
      });
    return () => {
      isCurrent = false;
    };
  }, [api, isOffline, localBudgetId, subject, workspaceId]);

  async function createBudget(): Promise<void> {
    if (
      workspaceId === null ||
      localBudgetId === null ||
      localBudgetName === null ||
      isOffline
    ) {
      throw new Error('Cloud budget registration requires connectivity.');
    }
    const created = await api.createBudget(workspaceId, {
      name: localBudgetName,
    });
    saveCloudBudgetId(subject, workspaceId, localBudgetId, created.id);
    setState(current => ({
      budgets: [
        ...current.budgets.filter(budget => budget.id !== created.id),
        created,
      ],
      error: null,
      selectedBudgetId: created.id,
      status: 'ready',
    }));
  }

  function selectBudget(budgetId: string): void {
    if (workspaceId === null || localBudgetId === null || isOffline) return;
    const selected = state.budgets.find(
      budget => budget.id === budgetId && budget.status === 'active',
    );
    if (!selected) return;
    saveCloudBudgetId(subject, workspaceId, localBudgetId, selected.id);
    setState(current => ({
      ...current,
      error: null,
      selectedBudgetId: selected.id,
    }));
  }

  return { ...state, createBudget, reload, selectBudget };
}

function initialState(
  subject: string,
  workspaceId: string | null,
  localBudgetId: string | null,
): BindingState {
  return {
    budgets: [],
    error: null,
    selectedBudgetId:
      workspaceId === null || localBudgetId === null
        ? null
        : readCloudBudgetId(subject, workspaceId, localBudgetId),
    status: 'idle',
  };
}

function resolveCloudBudgetId(
  subject: string,
  workspaceId: string,
  localBudgetId: string,
  budgets: readonly CloudBudget[],
): string | null {
  const selectedBudgetId = loadCloudBudgetId(
    subject,
    workspaceId,
    localBudgetId,
    budgets,
  );
  if (selectedBudgetId === null) {
    clearCloudBudgetId(subject, workspaceId, localBudgetId);
  } else {
    saveCloudBudgetId(
      subject,
      workspaceId,
      localBudgetId,
      selectedBudgetId,
    );
  }
  return selectedBudgetId;
}

function toError(value: unknown): Error {
  return value instanceof Error
    ? value
    : new Error('Unexpected cloud budget error.');
}
